/**
 * ProCalculator Backend API Client
 *
 * GraphQL operations for Advanced Calculator Lambda functions:
 * - generateParametersFromTariff: Auto-generate invoice parameters from TAURON pricing
 * - calculateProfessionalTariffs: Simulate all 6 tariffs and recommend cheapest
 *
 * @see /docs/PROFESSIONAL_CALCULATOR_TESTING.md - Complete API documentation
 * @see /amplify/data/generateParametersFromTariff/handler.ts - Parameter generation logic
 * @see /amplify/data/calculateProfessionalTariffs/handler.ts - Tariff simulation logic
 */

import { generateClient } from 'aws-amplify/api';
import type { TariffType, MonthlyConfiguration } from '../../types/proCalculator';

// Define GraphQL result type locally since @aws-amplify/api-graphql is not a direct dependency
// This matches the structure returned by aws-amplify v6 generateClient().graphql()
interface GraphQLResult<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

// CRITICAL: authMode configuration for authenticated requests
const AUTH_MODE = 'userPool' as const;

/**
 * Invoice Parameters (11 values from electricity bill)
 * Values in PLN with VAT (23%)
 */
export interface InvoiceParameters {
  energyElectricCost: number;
  handlingFee: number;
  networkFixedFee: number;
  transitionFee: number;
  qualityFee: number;
  networkVariableFee: number;
  ozeFee: number;
  cogenerationFee: number;
  subscriptionFee: number;
  powerFee: number;
}

/**
 * Tariff Simulation Result (backend response)
 */
export interface TariffSimulation {
  annualCost: number;
  monthlyCost: number;
  savings: number;
  isRecommended: boolean;
  breakdown: {
    energyCost: number;
    distributionCost: number;
    fixedCosts: number;
  };
  monthlyBreakdown: Array<{
    monthIndex: number;        // 0-11 (Jan-Dec)
    totalCost: number;         // PLN (gross)
    energyCost: number;        // PLN (gross)
    distributionCost: number;  // PLN (gross)
    fixedCost: number;         // PLN (gross)
  }>;
}

/**
 * Calculate Professional Tariffs Response
 * Note: G14 is optional and only included if user has RCE price data
 */
export interface CalculateProfessionalTariffsResponse {
  simulations: {
    G11: TariffSimulation;
    G12: TariffSimulation;
    G12w: TariffSimulation;
    G12as: TariffSimulation;
    G12n?: TariffSimulation;             // Optional - PGE-specific
    G12r?: TariffSimulation;             // Optional - Energa-specific
    G13: TariffSimulation;
    G13s: TariffSimulation;
    G14?: TariffSimulation & {
      rcePriceStats?: {
        avgPricePerKwh: number;
        minPricePerKwh: number;
        maxPricePerKwh: number;
      };
    };
  };
  recommendedTariff: TariffType;
  potentialSavings: number;
  tariffCategory?: 'HOUSEHOLD' | 'BUSINESS';
  debug?: {
    monthlyPatterns: Array<{
      monthIndex: number;
      monthName: string;
      tariff: string;
      hourlyPatternType: string;
      patternWeekdaySum: number;
      patternWeekendSum: number;
      patternWeekdayFirst3: number[];
      patternWeekdayLast3: number[];
    }>;
  };
}

/**
 * Generate Parameters from Tariff Input
 */
export interface GenerateParametersInput {
  tariff: TariffType;
  monthlyConsumption: number;
  monthIndex: number;
}

/**
 * Calculate Professional Tariffs Input
 */
export interface CalculateProfessionalTariffsInput {
  months: Array<{
    monthIndex: number;
    consumption: number;
    tariff: TariffType;
    hourlyPattern: 'UNIFORM' | 'MORNINGS_EVENINGS' | 'WORK_FROM_HOME' | 'NIGHT_ELECTRIC' | 'FROM_CSV_IMPORT';
    parameters: InvoiceParameters;
  }>;
  hourlyPatternFromBasic?: {
    weekday: number[];
    weekend: number[];
  };
  hourlyPatternFromCSV?: Record<number, {
    weekday: number[];
    weekend: number[];
  }>;
  tariffCategory?: 'HOUSEHOLD' | 'BUSINESS';
  contractedPower?: number;
  businessPowerGroup?: 'C1' | 'C2';
}

/**
 * Generate invoice parameters for a specific month
 *
 * Auto-generates (a) values from TAURON_2025_PRICING when user has no SavedCalculation.
 * Used to populate initial parameter values before user overrides.
 *
 * @param tariff - Tariff type (G11, G12, G12w, G12as, G13, G13s)
 * @param monthlyConsumption - Monthly consumption in kWh
 * @param monthIndex - Month index (0-11, where 0 = January)
 * @returns Invoice parameters with all 11 values in PLN with VAT
 *
 * @example
 * const params = await generateParametersFromTariff('G11', 300, 0);
 * // Returns: { energyElectricCost: 186.01, handlingFee: 9.30, ... }
 */
export async function generateParametersFromTariff(
  tariff: TariffType,
  monthlyConsumption: number,
  monthIndex: number
): Promise<InvoiceParameters> {
  const client = generateClient();

  // Use raw GraphQL query for custom query (not accessible via typed client.queries)
  // CRITICAL: authMode must be specified for authenticated requests
  const response = (await client.graphql({
    query: `
      query GenerateParameters($tariff: String!, $consumption: Float!, $month: Int!) {
        generateParametersFromTariff(
          tariff: $tariff
          monthlyConsumption: $consumption
          monthIndex: $month
        )
      }
    `,
    variables: {
      tariff,
      consumption: monthlyConsumption,
      month: monthIndex,
    },
    authMode: AUTH_MODE,
  })) as GraphQLResult<{ generateParametersFromTariff: string }>;

  const { data, errors } = response;

  if (errors && errors.length > 0) {
    throw new Error(errors.map((e: { message: string }) => e.message).join(', '));
  }

  if (!data) {
    throw new Error('No data returned from generateParametersFromTariff');
  }

  // Parse JSON response from Lambda
  // Handle potential double-JSON encoding from AppSync
  const rawResult = data.generateParametersFromTariff;

  let parameters: InvoiceParameters;
  if (typeof rawResult === 'string') {
    // Data is JSON string, parse it
    parameters = JSON.parse(rawResult) as InvoiceParameters;
  } else {
    // Data is already parsed (shouldn't happen but handle it)
    parameters = rawResult as InvoiceParameters;
  }

  return parameters;
}

/**
 * Calculate professional tariff simulations for all 6 tariffs
 *
 * Runs simulations for G11, G12, G12w, G12as, G13, G13s tariffs.
 * Recommends cheapest tariff and calculates potential savings.
 *
 * @param months - Array of 12 monthly configurations
 * @param hourlyPatternFromBasic - Optional custom hourly pattern from SavedCalculation
 * @returns Simulation results for all 6 tariffs with recommendation
 *
 * @example
 * const input = {
 *   months: [
 *     {
 *       monthIndex: 0,
 *       consumption: 300,
 *       tariff: 'G11',
 *       hourlyPattern: 'UNIFORM',
 *       parameters: { energyElectricCost: 186.01, ... },
 *     },
 *     // ... 11 more months
 *   ],
 * };
 * const results = await calculateProfessionalTariffs(input);
 * // Returns: { simulations: { G11: {...}, G12: {...}, ... }, recommendedTariff: 'G12', ... }
 */
export async function calculateProfessionalTariffs(
  input: CalculateProfessionalTariffsInput
): Promise<CalculateProfessionalTariffsResponse> {
  const client = generateClient();

  // Use raw GraphQL mutation for custom mutation (not accessible via typed client.mutations)
  // CRITICAL: authMode must be specified for authenticated requests
  const response = (await client.graphql({
    query: `
      mutation CalculateProfessionalTariffs($input: AWSJSON!) {
        calculateProfessionalTariffs(input: $input)
      }
    `,
    variables: {
      input: JSON.stringify(input),
    },
    authMode: AUTH_MODE,
  })) as GraphQLResult<{ calculateProfessionalTariffs: string }>;

  const { data, errors } = response;

  if (errors && errors.length > 0) {
    throw new Error(errors.map((e: { message: string }) => e.message).join(', '));
  }

  if (!data) {
    throw new Error('No data returned from calculateProfessionalTariffs');
  }

  // Parse JSON response from Lambda
  // AppSync returns JSON string, need to parse it
  let results: CalculateProfessionalTariffsResponse;

  // Access the mutation result from data
  const mutationResult = data.calculateProfessionalTariffs;

  if (typeof mutationResult === 'string') {
    // Data is JSON string, parse it
    results = JSON.parse(mutationResult) as CalculateProfessionalTariffsResponse;
  } else {
    // Data is already parsed (shouldn't happen but handle it)
    results = mutationResult as CalculateProfessionalTariffsResponse;
  }

  return results;
}

/**
 * Generate parameters for all 12 months
 *
 * Utility function to generate parameters for an entire year.
 * Useful for initializing or regenerating all month parameters.
 *
 * @param months - Array of 12 monthly configurations (tariff, consumption, pattern)
 * @returns Array of 12 InvoiceParameters
 *
 * @example
 * const monthlyConfigs = [
 *   { tariff: 'G11', consumption: 300, monthIndex: 0 },
 *   // ... 11 more months
 * ];
 * const allParameters = await generateParametersForAllMonths(monthlyConfigs);
 */
export async function generateParametersForAllMonths(
  months: Array<{ tariff: TariffType; consumption: number; monthIndex: number }>
): Promise<InvoiceParameters[]> {
  // Generate parameters for all months in parallel
  const promises = months.map((month) =>
    generateParametersFromTariff(month.tariff, month.consumption, month.monthIndex)
  );

  const results = await Promise.all(promises);

  // CRITICAL FIX: Parse each result if it's still a JSON string
  // This handles cases where AppSync double-encodes JSON or browser cache issues
  return results.map(result => {
    if (typeof result === 'string') {
      return JSON.parse(result) as InvoiceParameters;
    }
    return result;
  });
}

/**
 * Normalize hourly pattern values from percentages (0-100) to fractions (0-1)
 *
 * Frontend stores hourly patterns as percentages (sum ~100) for UI convenience,
 * but backend expects fractions (sum ~1.0) for calculations.
 *
 * @param pattern - Hourly pattern with weekday and weekend arrays
 * @returns Normalized pattern with values between 0 and 1
 */
function normalizeHourlyPattern(
  pattern: { weekday: number[]; weekend: number[] }
): { weekday: number[]; weekend: number[] } {
  // Check if values appear to be percentages (sum > 2 indicates percentage format)
  const weekdaySum = pattern.weekday.reduce((sum, v) => sum + v, 0);
  const weekendSum = pattern.weekend.reduce((sum, v) => sum + v, 0);

  // If sum is significantly > 1 (e.g., close to 100), values are in percentage format
  const isWeekdayPercentage = weekdaySum > 2;
  const isWeekendPercentage = weekendSum > 2;

  return {
    weekday: isWeekdayPercentage
      ? pattern.weekday.map(v => v / 100)
      : pattern.weekday,
    weekend: isWeekendPercentage
      ? pattern.weekend.map(v => v / 100)
      : pattern.weekend,
  };
}

/**
 * Prepare input for calculateProfessionalTariffs from MonthlyConfiguration array
 *
 * Converts frontend MonthlyConfiguration format to backend API input format.
 * Handles the 4-value system by using valueFinal for each parameter.
 * IMPORTANT: Normalizes hourly patterns from percentages (0-100) to fractions (0-1).
 *
 * @param months - Array of 12 MonthlyConfiguration from frontend form
 * @param hourlyPatternFromBasic - Optional pattern from Basic Calculator (may be in percentage format)
 * @param hourlyPatternFromCSV - Optional patterns from CSV import (per month, may be in percentage format)
 * @returns Input ready for calculateProfessionalTariffs mutation
 */
export function prepareCalculationInput(
  months: MonthlyConfiguration[],
  hourlyPatternFromBasic?: { weekday: number[]; weekend: number[] },
  hourlyPatternFromCSV?: Record<number, { weekday: number[]; weekend: number[] }>
): CalculateProfessionalTariffsInput {
  // Normalize hourlyPatternFromBasic if provided
  const normalizedBasicPattern = hourlyPatternFromBasic
    ? normalizeHourlyPattern(hourlyPatternFromBasic)
    : undefined;

  // Normalize hourlyPatternFromCSV if provided
  const normalizedCSVPatterns = hourlyPatternFromCSV
    ? Object.fromEntries(
        Object.entries(hourlyPatternFromCSV).map(([monthIndex, pattern]) => [
          monthIndex,
          normalizeHourlyPattern(pattern),
        ])
      )
    : undefined;

  return {
    months: months.map((month, index) => ({
      monthIndex: index,
      consumption: month.consumption,
      tariff: month.tariff,
      hourlyPattern: month.hourlyPattern,
      parameters: {
        energyElectricCost: month.parameters.energyElectricCost.valueFinal,
        handlingFee: month.parameters.handlingFee.valueFinal,
        networkFixedFee: month.parameters.networkFixedFee.valueFinal,
        transitionFee: month.parameters.transitionFee.valueFinal,
        qualityFee: month.parameters.qualityFee.valueFinal,
        networkVariableFee: month.parameters.networkVariableFee.valueFinal,
        ozeFee: month.parameters.ozeFee.valueFinal,
        cogenerationFee: month.parameters.cogenerationFee.valueFinal,
        subscriptionFee: month.parameters.subscriptionFee.valueFinal,
        powerFee: month.parameters.powerFee.valueFinal,
      },
    })),
    hourlyPatternFromBasic: normalizedBasicPattern,
    hourlyPatternFromCSV: normalizedCSVPatterns,
  };
}
